import { create } from "zustand";
import type { BeforeInstallPromptEvent } from "../utils/pwaUtils";
import { runInstallPrompt, markInstalled, suppressPrompt } from "../utils/pwaPromptState";

type InstallResult = "accepted" | "dismissed" | "error" | "unavailable";

// Shared UI state for the PWA install flow so the centralised modal and the
// header "Install app" menu entry drive the same prompt and the single captured
// beforeinstallprompt event.
interface PwaInstallState {
  // Set once when Chromium fires beforeinstallprompt; null on iOS (never fires)
  // and after a completed/declined native prompt.
  deferredPrompt: BeforeInstallPromptEvent | null;
  isModalOpen: boolean;
  setDeferredPrompt: (deferredPrompt: BeforeInstallPromptEvent | null) => void;
  openModal: () => void;
  closeModal: () => void;
  // Run the captured native prompt and reconcile the modal in one place.
  // beforeinstallprompt is single-use, so the prompt is always cleared
  // afterwards; on a dismissal we start the cooldown (so we don't nag) instead
  // of leaving a broken "Install" affordance that would reopen a dead-end modal.
  triggerInstall: () => Promise<InstallResult>;
}

const usePwaInstallStore = create<PwaInstallState>((set, get) => ({
  deferredPrompt: null,
  isModalOpen: false,
  setDeferredPrompt: (deferredPrompt) => set({ deferredPrompt }),
  openModal: () => set({ isModalOpen: true }),
  closeModal: () => set({ isModalOpen: false }),
  triggerInstall: async () => {
    const { deferredPrompt } = get();
    // No captured prompt (iOS, or already spent) — callers should be showing
    // manual instructions instead of invoking this.
    if (!deferredPrompt) return "unavailable";

    const outcome = await runInstallPrompt(deferredPrompt);
    // The prompt is single-use; clear it and close the modal regardless of
    // outcome so nothing keeps advertising an install that can't fire again.
    set({ deferredPrompt: null, isModalOpen: false });

    if (outcome === "accepted") {
      markInstalled();
    } else if (outcome === "dismissed") {
      // User declined the native dialog — respect it like a "Later".
      suppressPrompt();
    }
    return outcome;
  },
}));

export default usePwaInstallStore;
